import { useMemo } from 'react';
import { PoolTeam } from '../types/pool';
import { CountryFlag } from './CountryFlag';

/**
 * WorldCupTeamPicker — three-view team selection grid for World Cup pools.
 *
 *   Seeds   — 12 FIFA seed tiers, 4 teams each (risk/reward lens)
 *   Groups  — 12 World Cup groups (A–L), 4 teams each (competitive context lens)
 *   Bracket — 16 R32 matchup slots with live team data when populated
 *             (path-to-final lens)
 *
 * Used by WorldCupPick6EntryForm and WorldCupSurvivorEntryForm. The same
 * component handles both because the picker logic is identical — what differs
 * is which teams are selected, which are disabled (one-and-done), and the
 * selection limit.
 */

export type WorldCupView = 'seeds' | 'groups' | 'bracket';

/**
 * Pair of teams playing in a single knockout match — passed in from the entry
 * form when actual matchups are known (post-group-stage). Each team is the same
 * PoolTeam shape the picker already uses, so we can reuse rendering. `null`
 * means that slot's team isn't filled yet.
 */
export interface WorldCupMatchup {
  eventId: string;
  /** Display label (e.g. "R32 Match #79", "Quarter-Final #97"). Falls back to round + index. */
  label?: string;
  /** ISO timestamp of kickoff (UTC). Rendered in the user's local time on each match card. */
  scheduledAt?: string | null;
  home: PoolTeam | null;
  away: PoolTeam | null;
}

export interface WorldCupTeamPickerProps {
  teams: PoolTeam[];
  selectedTeamIds: Set<string>;
  /** Teams disabled due to one-and-done (Survivor Phase 2). */
  disabledTeamIds?: Set<string>;
  /** Hard cap on how many teams can be selected (Pick 6 = 6, Survivor Phase 1 = 6). */
  maxSelections?: number;
  /** Disables all interaction (e.g. deadline passed, eliminated entry). */
  readOnly?: boolean;
  onToggle: (team: PoolTeam) => void;
  view: WorldCupView;
  onViewChange: (view: WorldCupView) => void;
  /**
   * Optional title to display above the tabs (e.g. "Pick your 6 group-stage survivors").
   * If omitted, just the tabs render.
   */
  helpText?: string;
  /**
   * Optional matchups for the "Games" view. When provided, the Bracket/Games tab
   * renders the actual match cards (home vs away, click team to pick) instead of
   * the static slot-label layout. Used by Survivor Phase 2 once admin has populated
   * R32/R16/etc. event team data.
   */
  matchups?: WorldCupMatchup[];
}

const GROUP_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'] as const;

/**
 * 2026 FIFA World Cup R32 matchup structure (slot labels).
 * Source: 2026-world-cup-schedule-import.json — match numbers 73–88.
 */
const R32_MATCHUPS: { num: number; slot1: string; slot2: string }[] = [
  { num: 73, slot1: '2A',  slot2: '2B' },
  { num: 74, slot1: '1E',  slot2: '3 ABCDF' },
  { num: 75, slot1: '1F',  slot2: '2C' },
  { num: 76, slot1: '1C',  slot2: '2F' },
  { num: 77, slot1: '1I',  slot2: '3 CDFGH' },
  { num: 78, slot1: '2E',  slot2: '2I' },
  { num: 79, slot1: '1A',  slot2: '3 CEFHI' },
  { num: 80, slot1: '1L',  slot2: '3 EHIJK' },
  { num: 81, slot1: '1D',  slot2: '3 BEFIJ' },
  { num: 82, slot1: '1G',  slot2: '3 AEHIJ' },
  { num: 83, slot1: '2K',  slot2: '2L' },
  { num: 84, slot1: '1H',  slot2: '2J' },
  { num: 85, slot1: '1B',  slot2: '3 EFGIJ' },
  { num: 86, slot1: '1J',  slot2: '2H' },
  { num: 87, slot1: '1K',  slot2: '3 DEIJL' },
  { num: 88, slot1: '2D',  slot2: '2G' },
];

function tierColors(seed: number): { container: string; bg: string; text: string; ring: string } {
  if (seed <= 4)  return { container: 'border-emerald-300', bg: 'bg-emerald-50', text: 'text-emerald-900', ring: 'ring-emerald-400' };
  if (seed <= 8)  return { container: 'border-sky-300',     bg: 'bg-sky-50',     text: 'text-sky-900',     ring: 'ring-sky-400' };
  return            { container: 'border-rose-300',    bg: 'bg-rose-50',    text: 'text-rose-900',    ring: 'ring-rose-400' };
}

export function WorldCupTeamPicker(props: WorldCupTeamPickerProps) {
  const {
    teams,
    selectedTeamIds,
    disabledTeamIds = new Set(),
    maxSelections,
    readOnly = false,
    onToggle,
    view,
    onViewChange,
    helpText,
    matchups,
  } = props;

  const teamsByGroup = useMemo(() => {
    const grouped: Record<string, PoolTeam[]> = {};
    for (const t of teams) {
      const g = (t.metadata?.group as string) || 'Unknown';
      if (!grouped[g]) grouped[g] = [];
      grouped[g].push(t);
    }
    for (const g of Object.keys(grouped)) {
      grouped[g].sort((a, b) => (a.seed ?? 99) - (b.seed ?? 99));
    }
    return grouped;
  }, [teams]);

  const teamsBySeed = useMemo(() => {
    const grouped: Record<number, PoolTeam[]> = {};
    for (const t of teams) {
      const s = t.seed ?? 12;
      if (!grouped[s]) grouped[s] = [];
      grouped[s].push(t);
    }
    for (const s of Object.keys(grouped)) {
      grouped[Number(s)].sort((a, b) =>
        (a.team.shortName || a.team.name).localeCompare(b.team.shortName || b.team.name),
      );
    }
    return grouped;
  }, [teams]);

  // Map slot label (e.g. "1A", "2B") → expected team if known. We only know the
  // 1-of-each-group placeholder mapping pre-tournament; actual teams resolve
  // post-group-stage when admin populates events. For now we leave slots blank
  // and surface group context.
  const teamsByGroupAndPosition = useMemo(() => teamsByGroup, [teamsByGroup]);

  function isAtCap(team: PoolTeam) {
    if (!maxSelections) return false;
    if (selectedTeamIds.has(team.team.id)) return false;
    return selectedTeamIds.size >= maxSelections;
  }

  function renderTeamButton(t: PoolTeam, opts: { compact?: boolean } = {}) {
    const id = t.team.id;
    const seed = t.seed ?? 12;
    const tier = tierColors(seed);
    const isSelected = selectedTeamIds.has(id);
    const isDisabled = disabledTeamIds.has(id);
    const isAtCapacity = isAtCap(t);
    const interactDisabled = readOnly || isDisabled || (!isSelected && isAtCapacity);
    const iso = (t.metadata?.isoCode as string) || '';

    return (
      <button
        key={id}
        type="button"
        onClick={() => onToggle(t)}
        disabled={interactDisabled}
        title={
          isDisabled
            ? 'Already used earlier in this entry'
            : isAtCapacity
            ? `Selection limit (${maxSelections}) reached`
            : t.team.name
        }
        className={`px-2.5 py-1.5 rounded text-sm font-medium transition-all text-left flex items-center gap-2 border ${
          isSelected
            ? `${tier.bg} ${tier.text} border-2 ${tier.container} ring-2 ${tier.ring}`
            : isDisabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed line-through border-gray-200'
            : isAtCapacity
            ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200'
            : 'bg-white hover:bg-gray-50 text-gray-800 border-gray-200'
        }`}
      >
        {iso && <CountryFlag iso={iso} alt={t.team.name} size="sm" rounded />}
        <span className="truncate flex-1">
          {opts.compact ? (t.team.shortName || t.team.name) : t.team.name}
        </span>
        <span className="text-xs text-gray-400 ml-auto">S{seed}</span>
        {isSelected && <span className="text-xs">✓</span>}
      </button>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header — help text + tab switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {helpText ? <p className="text-sm text-gray-600">{helpText}</p> : <span />}
        <div role="tablist" className="inline-flex bg-gray-100 rounded-lg p-1 gap-1 self-start">
          {([
            { key: 'seeds', label: 'Seeds' },
            { key: 'groups', label: 'Groups' },
            // Tab is labeled "Games" when matchups are wired up (real teams known)
            // and "Bracket" when only the static slot view is available.
            { key: 'bracket', label: matchups && matchups.length > 0 ? 'Games' : 'Bracket' },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={view === tab.key}
              onClick={() => onViewChange(tab.key)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                view === tab.key
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* SEEDS view */}
      {view === 'seeds' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((seed) => {
            const list = teamsBySeed[seed] || [];
            if (list.length === 0) return null;
            const tier = tierColors(seed);
            return (
              <div key={seed} className={`rounded-lg border-2 ${tier.container} overflow-hidden`}>
                <div className={`px-4 py-2 ${tier.bg} ${tier.text} flex items-center justify-between`}>
                  <h3 className="font-bold">Seed {seed}</h3>
                  <span className="text-xs opacity-70">FIFA tier</span>
                </div>
                <div className="p-3 bg-white grid grid-cols-2 gap-2">
                  {list.map((t) => renderTeamButton(t, { compact: true }))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* GROUPS view */}
      {view === 'groups' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GROUP_LETTERS.map((letter) => {
            const list = teamsByGroup[letter] || [];
            if (list.length === 0) return null;
            return (
              <div key={letter} className="rounded-lg border-2 border-gray-200 overflow-hidden bg-white">
                <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900">Group {letter}</h3>
                  <span className="text-xs text-gray-500">Top 2 + best 3rd → R32</span>
                </div>
                <div className="p-3 grid grid-cols-1 gap-2">
                  {list.map((t) => renderTeamButton(t))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* BRACKET / GAMES view */}
      {view === 'bracket' && (matchups && matchups.length > 0 ? (
        // GAMES view: actual matchups passed in from the entry form. Each card
        // is one game with both teams shown — user clicks the team they want
        // to pick. Much clearer than figuring out "which match is Mexico in?"
        // by scanning the groups.
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900">
            ⚽ This round's matchups. Click a team to pick the winner of that game.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {matchups.map((m, idx) => (
              <div key={m.eventId} className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <div className="text-xs text-gray-500 font-semibold truncate">
                    {m.label || `Match ${idx + 1}`}
                  </div>
                  {m.scheduledAt && (
                    <div className="text-[11px] text-gray-500 whitespace-nowrap" title={new Date(m.scheduledAt).toLocaleString()}>
                      {formatMatchKickoff(m.scheduledAt)}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <MatchupSide team={m.home} fallback="TBD" renderTeam={(t) => renderTeamButton(t, { compact: true })} />
                  <div className="text-center text-xs text-gray-400">vs</div>
                  <MatchupSide team={m.away} fallback="TBD" renderTeam={(t) => renderTeamButton(t, { compact: true })} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        // Fallback BRACKET view: static R32 slot labels (used pre-group-stage
        // or for Pick 6 where matchups aren't relevant).
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
            ⚽ Round of 32 matchup slots. Each slot fills in once the group stage finishes —
            <strong> 1A</strong> = winner of Group A, <strong>2B</strong> = runner-up of Group B,
            <strong> 3 ABCDF</strong> = a best-third-place team from one of those groups.
            Click a country in the slot to pick it directly (where teams are known).
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {R32_MATCHUPS.map((m) => {
              const slot1Teams = resolveSlotTeams(m.slot1, teamsByGroupAndPosition);
              const slot2Teams = resolveSlotTeams(m.slot2, teamsByGroupAndPosition);
              return (
                <div key={m.num} className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="text-xs text-gray-500 mb-2 font-semibold">R32 Match #{m.num}</div>
                  <div className="space-y-2">
                    <BracketSlot label={m.slot1} candidates={slot1Teams} renderTeam={(t) => renderTeamButton(t, { compact: true })} />
                    <div className="text-center text-xs text-gray-400">vs</div>
                    <BracketSlot label={m.slot2} candidates={slot2Teams} renderTeam={(t) => renderTeamButton(t, { compact: true })} />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 text-center pt-2">
            R16 → QF → SF → F structure follows standard 2026 World Cup bracket. Detailed
            knockout-round views appear once teams are populated by admin after group play.
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * BracketSlot — renders a single R32 slot. If the slot label is "1A" / "2B"
 * etc. and we know the teams in that group, list them as candidates the user
 * can pick from. If the slot is a 3rd-place wildcard like "3 ABCDF", list all
 * teams from those groups.
 */
function BracketSlot({
  label,
  candidates,
  renderTeam,
}: {
  label: string;
  candidates: PoolTeam[];
  renderTeam: (t: PoolTeam) => React.ReactNode;
}) {
  return (
    <div className="bg-gray-50 rounded p-2">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
        Slot {label}
      </div>
      {candidates.length === 0 ? (
        <p className="text-xs italic text-gray-400">No team data — populated post-group-stage.</p>
      ) : (
        <div className="grid grid-cols-1 gap-1.5">{candidates.map((t) => renderTeam(t))}</div>
      )}
    </div>
  );
}

/**
 * MatchupSide — one side of a real Games-view match card. Renders the
 * pickable team button when known, or a "TBD" placeholder when admin hasn't
 * yet populated that slot (e.g. the team that wins a feeder match).
 */
function MatchupSide({
  team,
  fallback,
  renderTeam,
}: {
  team: PoolTeam | null;
  fallback: string;
  renderTeam: (t: PoolTeam) => React.ReactNode;
}) {
  if (team) return <div>{renderTeam(team)}</div>;
  return (
    <div className="bg-gray-50 rounded p-2 text-xs italic text-gray-400 text-center">
      {fallback}
    </div>
  );
}

/**
 * Format a kickoff timestamp for the match card — compact local-time display
 * ("Sat Jun 27, 3:00 PM"). Browser's timezone, so users see games at their
 * own clock. Hover tooltip on the parent has the full toLocaleString.
 */
function formatMatchKickoff(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
}

/**
 * Map an R32 slot label to the candidate teams that could fill it.
 *   "1A"           → all 4 teams in Group A (one of them will be 1st)
 *   "2B"           → all 4 teams in Group B (one of them will be 2nd)
 *   "3 ABCDF"      → all teams in Groups A, B, C, D, F (a best-third comes from one)
 */
function resolveSlotTeams(
  slot: string,
  teamsByGroup: Record<string, PoolTeam[]>,
): PoolTeam[] {
  const trimmed = slot.trim();

  // Pattern: single-letter group (e.g. "1A", "2B")
  const singleGroupMatch = /^[12]([A-L])$/.exec(trimmed);
  if (singleGroupMatch) {
    return teamsByGroup[singleGroupMatch[1]] || [];
  }

  // Pattern: third-place wildcard (e.g. "3 ABCDF")
  const thirdPlaceMatch = /^3\s+([A-L]+)$/.exec(trimmed);
  if (thirdPlaceMatch) {
    const letters = thirdPlaceMatch[1].split('');
    const out: PoolTeam[] = [];
    for (const l of letters) {
      out.push(...(teamsByGroup[l] || []));
    }
    return out;
  }

  return [];
}

export default WorldCupTeamPicker;
